import React, { useState, useEffect } from "react";
import { useGame } from "../contexts/GameContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Swords, Trophy, Coins, Heart, RotateCcw, LogIn, LogOut, Cloud, Volume2, VolumeX, Settings as SettingsIcon, Users, User, Sparkles, BookOpen, ListChecks } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { playMusic, SFX, setMuted, getAudioSettings } from "../utils/audio";
import { Leaderboard } from "../components/game/Leaderboard";
import { StatsLeaderboard } from "../components/social/StatsLeaderboard";
import { SocialChatWidget } from "../components/social/SocialChatWidget";
import TutorialOverlay from "../components/game/TutorialOverlay";
import { useSocial } from "../contexts/SocialContext";
import { useMissions } from "../contexts/MissionsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MainMenu = () => {
  const { character, meta, resetSave, gameLoaded, cloudSyncing } = useGame();
  const { user, loading: authLoading, loginWithGoogle, logout } = useAuth();
  const { profile, unlockAchievement } = useProfile();
  const { connected, onlineCount } = useSocial();
  const { claimableCount: missionClaimable } = useMissions();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [audioMuted, setAudioMuted] = useState(getAudioSettings().muted);
  const [incomingCount, setIncomingCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    playMusic("menu");
    // Succès "Squatteur de menu" : rester 5 minutes sur le menu sans jouer
    const t = setTimeout(() => unlockAchievement?.("menu_dweller"), 5 * 60 * 1000);
    return () => clearTimeout(t);
  }, [unlockAchievement]);

  // Polling demandes amis (toutes les 30s)
  useEffect(() => {
    if (!user) return;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/friends`, { withCredentials: true });
        setIncomingCount((res.data.incoming || []).length);
      } catch (e) {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNewRun = () => {
    SFX.click();
    if (character) {
      setConfirmOpen(true);
      return;
    }
    navigate("/select");
  };

  const confirmNewRun = () => {
    SFX.click();
    resetSave();
    setConfirmOpen(false);
    navigate("/select");
  };

  const handleContinue = () => {
    SFX.click();
    navigate("/map");
  };

  const toggleMute = () => {
    SFX.click();
    const newMuted = !audioMuted;
    setMuted(newMuted);
    setAudioMuted(newMuted);
  };

  if (!gameLoaded) return <div className="h-full w-full flex items-center justify-center bg-slate-900 text-slate-300">Chargement...</div>;

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots relative overflow-hidden flex flex-col">
      {/* Top-right: profile + auth + audio + settings */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <button
          data-testid="goto-profile"
          onClick={() => { SFX.click(); navigate("/profile"); }}
          className="bg-fuchsia-500 text-white border-4 border-slate-950 rounded-xl px-2.5 py-1 cartoon-shadow flex items-center gap-1.5 font-display font-bold"
          title="Mon profil"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm">Niv. <span data-testid="menu-level">{profile?.level || 1}</span></span>
        </button>
        <button
          data-testid="goto-friends"
          onClick={() => { SFX.click(); navigate("/friends"); }}
          className="relative bg-slate-800 text-white border-4 border-slate-950 rounded-xl w-10 h-10 flex items-center justify-center cartoon-shadow"
          title="Amis"
        >
          <Users className="w-4 h-4" />
          {incomingCount > 0 && (
            <span data-testid="friend-notif-badge" className="absolute -top-1 -right-1 bg-rose-500 text-white border-2 border-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">
              {incomingCount}
            </span>
          )}
        </button>
        <button
          data-testid="goto-missions"
          onClick={() => { SFX.click(); navigate("/missions"); }}
          className="relative bg-slate-800 text-white border-4 border-slate-950 rounded-xl w-10 h-10 flex items-center justify-center cartoon-shadow"
          title="Missions"
        >
          <ListChecks className="w-4 h-4" />
          {missionClaimable > 0 && (
            <span data-testid="mission-notif-badge" className="absolute -top-1 -right-1 bg-emerald-500 text-slate-950 border-2 border-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold">
              {missionClaimable}
            </span>
          )}
        </button>
        <button
          data-testid="goto-settings"
          onClick={() => { SFX.click(); navigate("/settings"); }}
          className="bg-slate-800 text-white border-4 border-slate-950 rounded-xl w-10 h-10 flex items-center justify-center cartoon-shadow"
          title="Paramètres"
        >
          <SettingsIcon className="w-4 h-4" />
        </button>
        <button
          data-testid="toggle-mute"
          onClick={toggleMute}
          className="bg-slate-800 text-white border-4 border-slate-950 rounded-xl w-10 h-10 flex items-center justify-center cartoon-shadow"
          title={audioMuted ? "Activer le son" : "Couper le son"}
        >
          {audioMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        {!authLoading && (
          user ? (
            <div className="flex items-center gap-2 bg-slate-800 border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow-static">
              {user.picture && <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full border-2 border-slate-950" />}
              <span data-testid="user-name" className="text-sm font-bold max-w-[140px] truncate">{user.name}</span>
              {cloudSyncing && <Cloud className="w-3 h-3 text-sky-400 animate-pulse" />}
              <button data-testid="logout-btn" onClick={() => { SFX.click(); logout(); }} className="ml-1 text-rose-400 hover:text-rose-300" title="Déconnexion">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              data-testid="login-btn"
              onClick={() => { SFX.click(); loginWithGoogle(); }}
              className="bg-white text-slate-950 border-4 border-slate-950 rounded-xl px-3 py-1.5 cartoon-shadow flex items-center gap-2 font-bold text-sm"
            >
              <LogIn className="w-4 h-4" /> Se connecter
            </button>
          )
        )}
      </div>
      {/* Decoration */}
      <div className="absolute top-10 -left-20 w-72 h-72 bg-fuchsia-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-10 -right-20 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl" />

      <div className="flex-1 overflow-y-auto relative z-10">
        <div className="min-h-full w-full flex flex-col items-center justify-center p-6 py-12 max-w-5xl mx-auto">
        {/* Title */}
        <div className="text-center mb-8 animate-slide-up">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-50 mb-3" style={{ textShadow: "4px 4px 0 #0f172a, 8px 8px 0 rgba(56,189,248,0.4)" }}>
            CHECK<span className="text-sky-400">POINT</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto">
            Descend les Donjons, affronte tes amis en co-op, défie le monde en PvP classé.
          </p>
          {connected && (
            <div data-testid="menu-online-count" className="mt-3 inline-flex items-center gap-2 bg-slate-800 border-2 border-slate-950 rounded-full px-3 py-1 cartoon-shadow-static">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
              </span>
              <span className="text-sm font-bold text-emerald-300">{onlineCount} joueur{onlineCount > 1 ? "s" : ""} en ligne</span>
            </div>
          )}
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8 w-full max-w-2xl">
          <div data-testid="meta-runs" className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static text-center">
            <RotateCcw className="w-5 h-5 mx-auto text-sky-400 mb-1" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Runs</p>
            <p className="font-display text-2xl font-bold">{meta.totalRuns}</p>
          </div>
          <div data-testid="meta-floor" className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static text-center">
            <Trophy className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Étage Max</p>
            <p className="font-display text-2xl font-bold">{meta.highestFloor}</p>
          </div>
          <div data-testid="meta-gold" className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static text-center">
            <Coins className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Or Total</p>
            <p className="font-display text-2xl font-bold">{meta.totalGold}</p>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
          {character && (
            <button
              data-testid="continue-btn"
              onClick={handleContinue}
              className="flex-1 bg-emerald-500 text-slate-950 font-display font-bold uppercase tracking-wider py-4 px-6 rounded-2xl border-4 border-slate-950 cartoon-shadow hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0_rgba(0,0,0,1)] flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" />
              Continuer
            </button>
          )}
          <button
            data-testid="new-run-btn"
            onClick={handleNewRun}
            className="flex-1 bg-sky-400 text-slate-950 font-display font-bold uppercase tracking-wider py-4 px-6 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2"
          >
            <Swords className="w-5 h-5" />
            Nouvelle Aventure
          </button>
        </div>
        <div className="mt-3 w-full max-w-md flex gap-3">
          <button
            data-testid="coop-btn"
            onClick={() => { SFX.click(); navigate("/lobby"); }}
            className="flex-1 bg-fuchsia-500 text-white font-display font-bold uppercase tracking-wider py-3 px-4 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2"
          >
            <Users className="w-5 h-5" />
            Co-op
          </button>
          <button
            data-testid="pvp-btn"
            onClick={() => { SFX.click(); navigate("/pvp"); }}
            className="flex-1 bg-rose-500 text-white font-display font-bold uppercase tracking-wider py-3 px-4 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2"
          >
            <Swords className="w-5 h-5" />
            PvP
          </button>
          <button
            data-testid="menu-shop-btn"
            onClick={() => { SFX.click(); navigate("/shop-menu"); }}
            className="flex-1 bg-yellow-400 text-slate-950 font-display font-bold uppercase tracking-wider py-3 px-4 rounded-2xl border-4 border-slate-950 cartoon-shadow flex items-center justify-center gap-2"
          >
            <Coins className="w-5 h-5" />
            Boutique
          </button>
        </div>

        {/* Astuce plein écran */}
        <p data-testid="fullscreen-hint" className="mt-4 text-xs text-slate-400 flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-slate-800 border-2 border-slate-700 font-bold text-slate-200 text-[11px]">F11</span>
          Appuie pour passer en plein écran et profiter de tout l'espace de jeu
        </p>

        {/* Bouton tutoriel (rejouable) */}
        <button
          data-testid="tutorial-btn"
          onClick={() => { SFX.click(); setShowTutorial(true); }}
          className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cyan-300 hover:text-cyan-200 bg-slate-800/70 border-2 border-slate-700 rounded-xl px-4 py-2 transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          Revoir le tutoriel
        </button>

        {/* Leaderboard PvP */}
        <div className="mt-8 w-full max-w-md">
          <Leaderboard limit={10} />
        </div>

        {/* Records mondiaux (runs / étage / dégâts) */}
        <div className="mt-4 w-full max-w-md">
          <StatsLeaderboard limit={5} />
        </div>

        {/* Aperçu des classes */}
        <div className="mt-12 w-full max-w-4xl">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">7 Classes Disponibles</p>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.values(CLASSES).map((c) => (
              <div
                key={c.id}
                className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${c.couleur} border-4 border-slate-950 overflow-hidden cartoon-shadow-static animate-float`}
                title={c.nom}
                style={{ animationDelay: `${Math.random() * 2}s` }}
              >
                <img src={c.image} alt={c.nom} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      <SocialChatWidget />

      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-slate-800 border-4 border-slate-950 rounded-3xl cartoon-shadow-lg-static" data-testid="confirm-newrun-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-slate-50">Démarrer une nouvelle aventure ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Une run est en cours. Démarrer une nouvelle aventure abandonnera la run actuelle (les passifs des sanctuaires seront tout de même conservés).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="confirm-cancel" className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl cartoon-shadow">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-newrun" onClick={confirmNewRun} className="bg-sky-400 text-slate-950 border-4 border-slate-950 rounded-xl cartoon-shadow font-display font-bold">Recommencer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MainMenu;
